import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, Search, Filter, Phone, CheckCircle, 
  XCircle, Clock, RefreshCw, MessageCircle, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function CommunicationLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Filters
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [channelFilter, statusFilter]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '200');
      
      const response = await api.get(`/communication-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch communication logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/communication-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-emerald-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <MessageCircle className="h-4 w-4 text-slate-500" />;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return <MessageCircle className="h-4 w-4 text-emerald-600" />;
      case 'sms':
        return <Phone className="h-4 w-4 text-blue-600" />;
      case 'email':
        return <Mail className="h-4 w-4 text-purple-600" />;
      default:
        return <MessageSquare className="h-4 w-4 text-slate-500" />;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.recipient_name?.toLowerCase().includes(query) ||
      log.recipient_number?.includes(query) ||
      log.message_content?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Communication Logs</h1>
          <p className="text-slate-600">Track all WhatsApp, SMS, and email communications</p>
        </div>
        <Button 
          onClick={() => { fetchLogs(); fetchStats(); }}
          variant="outline"
          className="border-slate-300 text-slate-700 hover:bg-slate-100"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Sent</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-700 text-sm font-medium">WhatsApp</p>
                <p className="text-2xl font-bold text-emerald-900">{stats.whatsapp || 0}</p>
              </div>
              <MessageCircle className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 text-sm font-medium">SMS</p>
                <p className="text-2xl font-bold text-blue-900">{stats.sms || 0}</p>
              </div>
              <Phone className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-700 text-sm font-medium">Failed</p>
                <p className="text-2xl font-bold text-red-900">{stats.failed || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search by recipient or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-300 text-slate-900"
              />
            </div>
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-[150px] bg-white border-slate-300 text-slate-900">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white border-slate-300 text-slate-900">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A238F]"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Communication Logs</h3>
            <p className="text-slate-600">Messages will appear here once sent</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-200">
              {filteredLogs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getChannelIcon(log.channel)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{log.recipient_name}</span>
                          <Badge variant="outline" className={
                            log.status === 'sent' || log.status === 'delivered'
                              ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                              : log.status === 'failed'
                                ? 'border-red-500 text-red-700 bg-red-50'
                                : 'border-yellow-500 text-yellow-700 bg-yellow-50'
                          }>
                            {getStatusIcon(log.status)}
                            <span className="ml-1">{log.status}</span>
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">{log.recipient_number}</p>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                          {log.message_content}
                        </p>
                        {log.error_message && (
                          <p className="text-xs text-red-600 font-medium mt-1">{log.error_message}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500 whitespace-nowrap">
                      {new Date(log.sent_at).toLocaleDateString()}
                      <br />
                      <span className="text-slate-600">{new Date(log.sent_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              {selectedLog && getChannelIcon(selectedLog.channel)}
              Message Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 font-medium">Recipient</p>
                  <p className="text-slate-900 font-medium">{selectedLog.recipient_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">Phone</p>
                  <p className="text-slate-900">{selectedLog.recipient_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">Channel</p>
                  <Badge className="capitalize">{selectedLog.channel}</Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">Status</p>
                  <Badge variant={selectedLog.status === 'sent' ? 'default' : 'destructive'}>
                    {selectedLog.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium">Sent At</p>
                  <p className="text-slate-900">{new Date(selectedLog.sent_at).toLocaleString()}</p>
                </div>
                {selectedLog.template_used && (
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Template</p>
                    <p className="text-slate-900 capitalize">{selectedLog.template_used.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium mb-2">Message Content</p>
                <div className="bg-slate-100 rounded-lg p-3 border border-slate-200">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{selectedLog.message_content}</p>
                </div>
              </div>
              {selectedLog.error_message && (
                <div>
                  <p className="text-xs text-red-600 font-medium mb-1">Error</p>
                  <p className="text-sm text-red-700">{selectedLog.error_message}</p>
                </div>
              )}
              {selectedLog.twilio_sid && (
                <div>
                  <p className="text-xs text-slate-500 font-medium">Twilio SID</p>
                  <p className="text-xs text-slate-600 font-mono">{selectedLog.twilio_sid}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
